import React, { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Container, Graphics } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import { isMobile } from '../../../../core/helpers/isMobile';
import { menuSectionStore } from '../../../../store/menuSectionStore';
import PayTableFooter from './payTableFooter';

let sliderStartX = 0;
let sliderStartY = 0;
let difference = 0;
let slideAnimation = null;

const PayTableWrapper = observer(({ PayTableContent, payTableStepCount, payTableContentWidth }) => {
  const { currentPayTableStepCount, setCurrentPayTableStepCount } = menuSectionStore;
  const [mask, setMask] = useState(null);
  const payTableContentWrapperRef = useRef();
  const payTableContainerRef = useRef();

  const handleSwitchSlide = useCallback(
    (stepCount = 0) => {
      const yOffset = isMobile() ? (!stepCount ? stepCount : -1400) : 0;
      slideAnimation = gsap.to(payTableContentWrapperRef.current, {
        duration: 0.7,
        x: isMobile() ? 0 : -stepCount * payTableContentWidth,
        y: yOffset,
      });
    },
    [payTableContentWidth, payTableContentWrapperRef]
  );

  useEffect(() => {
    if (payTableContainerRef.current) {
      payTableContainerRef.current.mask = mask;
    }
  }, [mask, payTableContainerRef]);

  useEffect(() => {
    if (!isMobile()) {
      handleSwitchSlide(currentPayTableStepCount);
    }
    return () => slideAnimation?.kill();
  }, [currentPayTableStepCount, handleSwitchSlide]);

  const handleMouseMove = useCallback((e) => {
    if (isMobile()) {
      payTableContentWrapperRef.current.y = e.data.global.y - difference;
    } else {
      payTableContentWrapperRef.current.x = e.data.global.x - difference;
    }
  }, []);

  const handlePointerDown = useCallback(
    (e) => {
      if (isMobile()) {
        sliderStartY = e.data.global.y;
        difference = e.data.global.y - payTableContentWrapperRef.current.y;
      } else {
        e.target.cursor = 'grabbing';
        sliderStartX = e.data.global.x;
        difference = e.data.global.x - payTableContentWrapperRef.current.x;
      }
      payTableContentWrapperRef.current.addEventListener('pointermove', handleMouseMove);
    },
    [payTableContentWrapperRef, handleMouseMove]
  );

  const handlePointerUp = useCallback(
    (e) => {
      payTableContentWrapperRef.current.removeEventListener('pointermove', handleMouseMove);
      const moveDistance = isMobile()
        ? sliderStartY - e.data.global.y
        : sliderStartX - e.data.global.x;

      if (!isMobile()) {
        e.target.cursor = 'pointer';
        const maxPage = payTableStepCount - 1;
        const minPage = 0;
        if (moveDistance > 0) {
          const nextPage = currentPayTableStepCount + 1;
          nextPage <= maxPage ? setCurrentPayTableStepCount(nextPage) : handleSwitchSlide(maxPage);
        } else if (moveDistance < 0) {
          const nextPage = currentPayTableStepCount - 1;
          nextPage >= minPage ? setCurrentPayTableStepCount(nextPage) : handleSwitchSlide(minPage);
        }
      } else if (payTableContentWrapperRef.current.y > 0) {
        handleSwitchSlide();
      } else if (payTableContentWrapperRef.current.y < -1400) {
        handleSwitchSlide(3);
      }

      difference = 0;
      sliderStartX = 0;
      sliderStartY = 0;
    },
    [
      payTableStepCount,
      currentPayTableStepCount,
      handleMouseMove,
      handleSwitchSlide,
      setCurrentPayTableStepCount,
    ]
  );

  return (
    <>
      <Container name='PayTableWrapperContainer' ref={payTableContainerRef}>
        {isMobile() && (
          <Graphics
            draw={(g) => {
              g.clear();
              g.beginFill(0xff700b);
              g.drawRect(0, 124, 540, 840);
            }}
            ref={(graphicsRef) => setMask(graphicsRef)}
          />
        )}
        <Container
          interactive
          cursor='pointer'
          name='PayTableContentWrapper'
          ref={payTableContentWrapperRef}
          pointerup={handlePointerUp}
          pointerdown={handlePointerDown}
          pointerupoutside={handlePointerUp}
        >
          <PayTableContent payTableContentWidth={payTableContentWidth} />
        </Container>
      </Container>
      <PayTableFooter
        x={isMobile() ? 0 : 611}
        y={isMobile() ? 866 : 750}
        payTableStepCount={payTableStepCount}
      />
    </>
  );
});

export default PayTableWrapper;
